// Role identifiers — must match backend exactly.
export const ROLES = {
    KAAFI_ADMIN: "kaafi_system_admin",
    KAAFI_HR: "kaafi_hr_manager",
    // Iter60.p42 — HR Officer. Same catalog as KAAFI_HR MINUS payroll write
    // actions and User Management. Nav visibility mirrors KAAFI_HR (see
    // NAV_ITEMS below) EXCEPT the `users` item which stays admin+hr_mgr.
    ASL_HR_OFFICER: "asl_hr_officer",
    // Iter60.p48 — Attendance Officer. Cross-module writer for attendance,
    // discipline, tasks, and PPE with read-only support permissions.
    // Explicitly blocked from payroll, payroll funding, wage rates,
    // contracts, module + master finance, shift-rule writes, reports
    // writes, and admin surfaces.
    ASL_ATTENDANCE_OFFICER: "asl_attendance_officer",
    ASL_SENIOR: "asl_senior_management",
    ASL_DEPT_MGR: "asl_department_manager",
    ASL_FINANCE: "asl_finance_viewer",
    ASL_VIEWER: "asl_read_only_viewer",
};

export const ROLE_LABELS = {
    kaafi_system_admin: "Kaafi System Admin",
    kaafi_hr_manager: "Kaafi HR Manager",
    asl_hr_officer: "ASL HR Officer",
    asl_attendance_officer: "ASL Attendance Officer",
    asl_senior_management: "ASL Senior Management",
    asl_department_manager: "ASL Department Manager",
    asl_finance_viewer: "ASL Finance Viewer",
    asl_read_only_viewer: "ASL Read-Only Viewer",
};

export function hasRole(user, ...allowed) {
    if (!user) return false;
    return allowed.includes(user.role);
}

export function canAccessModuleId(user, moduleId) {
    if (!user) return false;
    // Iter60.p50 — Attendance Officer is now module-scoped like a
    // Dept Manager. Cross-module URL access renders the 403 fallback.
    if (user.role === ROLES.ASL_DEPT_MGR || user.role === ROLES.ASL_ATTENDANCE_OFFICER) {
        return user.module_scope === moduleId;
    }
    return true;
}

export function canRecordManualAttendance(user) {
    return hasRole(user, ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER, ROLES.ASL_DEPT_MGR, ROLES.ASL_ATTENDANCE_OFFICER);
}

export function canEditEmployees(user) {
    // Iter60.p48 — Attendance Officer is READ-ONLY on employees.
    return hasRole(user, ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER);
}
export function canDeleteEmployees(user) {
    return hasRole(user, ROLES.KAAFI_ADMIN);
}
export function canApproveWageRate(user) {
    return hasRole(user, ROLES.KAAFI_ADMIN, ROLES.ASL_SENIOR);
}
export function canEditWageRate(user) {
    // Iter60.p48 — Attendance Officer is BLOCKED from wage rates entirely.
    return hasRole(user, ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER);
}

// Iter60.p48 — Shift rules: Attendance Officer has read access only.
export function canEditShiftRules(user) {
    return hasRole(user, ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER);
}

const WAGE_VIEW_ROLES = [
    ROLES.KAAFI_ADMIN,
    ROLES.KAAFI_HR,
    ROLES.ASL_HR_OFFICER,
    ROLES.ASL_SENIOR,
    ROLES.ASL_FINANCE,
    ROLES.ASL_DEPT_MGR,
];

// Iter60.p48 — Roles that see cross-module writer surfaces (attendance,
// exceptions, biometric, staff read, shift rules read, projects, leave
// read). Kept separate from WAGE_VIEW_ROLES because Attendance Officer
// is explicitly excluded from wages.
const ATTENDANCE_WORKFLOW_ROLES = [
    ROLES.KAAFI_ADMIN,
    ROLES.KAAFI_HR,
    ROLES.ASL_HR_OFFICER,
    ROLES.ASL_ATTENDANCE_OFFICER,
    ROLES.ASL_DEPT_MGR,
];

// Iter60.p50 — Nav visibility for all roles EXCEPT the Attendance
// Officer. Used on the "ALL"-style nav entries (Dashboard, Attendance,
// Staff Directory) where the officer needs a specific carve-out.
const ROLES_EXCEPT_ATTENDANCE_OFFICER = [
    ROLES.KAAFI_ADMIN,
    ROLES.KAAFI_HR,
    ROLES.ASL_HR_OFFICER,
    ROLES.ASL_SENIOR,
    ROLES.ASL_DEPT_MGR,
    ROLES.ASL_FINANCE,
    ROLES.ASL_VIEWER,
];

export const NAV_ITEMS = [
    {
        key: "dashboard",
        label: "Dashboard",
        path: "/dashboard",
        functional: true,
        // Iter60.p50 — Attendance Officer no longer sees Module
        // Dashboard. They land directly on `/m/cn/attendance` after
        // login (see App.js redirect).
        visibleTo: ROLES_EXCEPT_ATTENDANCE_OFFICER,
        moduleScoped: true,
    },
    {
        key: "staff",
        label: "Staff Directory",
        path: "/staff",
        functional: true,
        // Iter60.p50 — Staff Directory REMOVED from Attendance Officer
        // nav (spec — they see names via the attendance row hydrator,
        // no directory needed).
        visibleTo: ROLES_EXCEPT_ATTENDANCE_OFFICER,
        moduleScoped: true,
    },
    {
        key: "departments",
        label: "Departments & Worksites",
        path: "/departments",
        functional: true,
        visibleTo: "ALL",
    },
    {
        key: "contracts",
        label: "Contracts",
        path: "/contracts",
        functional: true,
        // Iter60.p48 — Attendance Officer is explicitly blocked from
        // Contracts. Enumerating every allowed role so the officer
        // never sees this nav item.
        visibleTo: [
            ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER,
            ROLES.ASL_SENIOR, ROLES.ASL_DEPT_MGR, ROLES.ASL_FINANCE,
            ROLES.ASL_VIEWER,
        ],
        moduleScoped: true,
    },
    {
        key: "attendance",
        label: "Attendance",
        path: "/attendance",
        functional: true,
        visibleTo: "ALL",
        moduleScoped: true,
    },
    {
        key: "exceptions",
        label: "Exception Queue",
        path: "/attendance/exceptions",
        functional: true,
        // Iter60.p48 — include ATTENDANCE_OFFICER for exception queue.
        visibleTo: [
            ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER,
            ROLES.ASL_ATTENDANCE_OFFICER, ROLES.ASL_DEPT_MGR,
        ],
        moduleScoped: true,
    },
    {
        key: "biometric",
        label: "Biometric Devices",
        path: "/biometric-devices",
        functional: true,
        // Iter60.p50 — REMOVED from Attendance Officer nav (previously
        // read-only in p48). Backend biometric sync/generate endpoints
        // also stripped the officer.
        visibleTo: [
            ROLES.KAAFI_ADMIN,
            ROLES.KAAFI_HR,
            ROLES.ASL_HR_OFFICER,
            ROLES.ASL_SENIOR,
            ROLES.ASL_DEPT_MGR,
        ],
        moduleScoped: true,
    },
    {
        // Iter85 — Biometric Ops: 3-tab view (Devices health, Events,
        // HR Exceptions backfill). Includes Attendance Officer because
        // the Exceptions tab is view-only for them.
        //
        // Labelled "Biometric Exceptions" to disambiguate from the
        // original Hikvision device-admin page above ("Biometric
        // Devices"). Both coexist — the original is for ongoing
        // Hikvision/device admin; this one is for the classification-
        // repair exceptions + HR backfill workflow.
        key: "biometric-ops",
        label: "Biometric Exceptions",
        path: "/biometric",
        functional: true,
        visibleTo: [
            ROLES.KAAFI_ADMIN,
            ROLES.KAAFI_HR,
            ROLES.ASL_HR_OFFICER,
            ROLES.ASL_ATTENDANCE_OFFICER,
            ROLES.ASL_DEPT_MGR,
        ],
        moduleScoped: true,
    },
    {
        key: "payroll",
        label: "Payroll",
        path: "/payroll",
        functional: true,
        // Iter60.p48 — Attendance Officer is BLOCKED from payroll.
        visibleTo: [
            ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER,
            ROLES.ASL_SENIOR, ROLES.ASL_FINANCE, ROLES.ASL_DEPT_MGR,
        ],
        moduleScoped: true,
    },
    {
        key: "payroll-funding",
        label: "Payroll Funding",
        path: "/payroll/funding",
        functional: true,
        // Iter60.p48 — Attendance Officer is BLOCKED from payroll funding.
        visibleTo: [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER, ROLES.ASL_FINANCE, ROLES.ASL_SENIOR],
        moduleScoped: true,
    },
    {
        key: "finance",
        label: "Finance",
        path: "/finance",
        functional: true,
        // Iter60.p48 — Attendance Officer is BLOCKED from module + master finance.
        visibleTo: [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER, ROLES.ASL_FINANCE, ROLES.ASL_SENIOR],
        moduleScoped: true,
    },
    {
        key: "wages",
        label: "Wage Rates",
        path: "/wage-rates",
        functional: true,
        // Iter60.p48 — Attendance Officer is BLOCKED from wage rates.
        visibleTo: WAGE_VIEW_ROLES,
        moduleScoped: true,
    },
    {
        key: "shift-rules",
        label: "Shift Rules",
        path: "/shift-rules",
        functional: true,
        // Iter60.p48 — Attendance Officer sees the page (READ) but the
        // page-level component hides write buttons via canEditShiftRules.
        visibleTo: [
            ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER,
            ROLES.ASL_ATTENDANCE_OFFICER, ROLES.ASL_SENIOR,
        ],
        moduleScoped: true,
    },
    {
        key: "reports",
        label: "Reports",
        path: "/reports",
        functional: true,
        // Iter60.p48 — Attendance Officer is BLOCKED from Reports per spec.
        visibleTo: [
            ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER,
            ROLES.ASL_SENIOR, ROLES.ASL_FINANCE, ROLES.ASL_DEPT_MGR, ROLES.ASL_VIEWER,
        ],
        moduleScoped: true,
    },
    {
        key: "audit",
        label: "Audit Log",
        path: "/audit",
        functional: true,
        visibleTo: [ROLES.KAAFI_ADMIN],
    },
    {
        // Iter60.p55b — top-level global incident directory. HR + Admin
        // only. Managers reach their module-scoped version via
        // `/m/{code}/incidents` in the sidebar nav.
        key: "incidents-global",
        label: "Incidents & Emergencies",
        path: "/incidents",
        functional: true,
        visibleTo: [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER],
    },
    {
        key: "users",
        label: "User Management",
        path: "/users",
        functional: true,
        visibleTo: [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR],
    },
    {
        key: "system-health",
        label: "System Health",
        path: "/admin/system-health",
        functional: true,
        visibleTo: [ROLES.KAAFI_ADMIN],
    },
    {
        // Iter76 — Cyber-Security Tier 2 admin dashboard.
        key: "security",
        label: "Security Dashboard",
        path: "/admin/security",
        functional: true,
        visibleTo: [ROLES.KAAFI_ADMIN],
    },
    {
        // Iter77 — Immutable audit log viewer (replaces the top-level
        // Audit Log nav item for admins, keeping the legacy one too).
        key: "audit-chain",
        label: "Audit Chain (Immutable)",
        path: "/admin/audit",
        functional: true,
        visibleTo: [ROLES.KAAFI_ADMIN],
    },
    {
        key: "settings",
        label: "System Settings",
        path: "/admin/settings",
        functional: true,
        visibleTo: [ROLES.KAAFI_ADMIN],
    },
    {
        // Iter84 — Payroll Trash. Super Admin only. Lists soft-deleted
        // periods across every module and offers Restore + Purge.
        key: "payroll-trash",
        label: "Payroll Trash",
        path: "/admin/payroll-trash",
        functional: true,
        visibleTo: [ROLES.KAAFI_ADMIN],
    },
    {
        key: "leave-types",
        label: "Leave Types (Law)",
        path: "/admin/settings/leave-types",
        functional: true,
        visibleTo: [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR],
    },
];

export function visibleNavFor(user, { excludeModuleScoped = false } = {}) {
    if (!user) return [];
    return NAV_ITEMS.filter((n) => {
        if (excludeModuleScoped && n.moduleScoped) return false;
        return n.visibleTo === "ALL" || n.visibleTo.includes(user.role);
    });
}

// Iter60.p50 — helpers used by App.js post-login redirect + Attendance
// page approve-button visibility.
export function isAttendanceOfficer(user) {
    return !!user && user.role === ROLES.ASL_ATTENDANCE_OFFICER;
}

export function canApproveAttendance(user) {
    // Attendance Officer stripped from approvals — Dept Manager
    // finalizes. Admin/HR retain approve.
    if (!user) return false;
    return hasRole(
        user,
        ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER,
        ROLES.ASL_DEPT_MGR,
    );
}

export function canCloseDiscipline(user) {
    // Iter60.p50 — Officer STRIPPED from `discipline.close`.
    if (!user) return false;
    return hasRole(user, ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR);
}

export function canCompleteTask(user) {
    // Iter60.p50 — Officer STRIPPED from `tasks.complete`.
    if (!user) return false;
    return hasRole(
        user,
        ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_DEPT_MGR,
    );
}

export function canSignoffPPE(user) {
    // Iter60.p50 — Officer keeps `ppe.write` (create/edit) but is
    // STRIPPED from `ppe.signoff` (return/replace).
    if (!user) return false;
    return hasRole(
        user,
        ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER, ROLES.ASL_DEPT_MGR,
    );
}

export const WAGE_VIEW_ROLE_LIST = WAGE_VIEW_ROLES;
export const ATTENDANCE_WORKFLOW_ROLE_LIST = ATTENDANCE_WORKFLOW_ROLES;
